/**
 * The delivery state of each answer this chat sends (mission stream contract
 * phase 4, agion/Agion#2327). The states are the generated DeliveryState table
 * and change only through nextDeliveryState (@agion/mission-stream):
 *
 * - sending: the frame left the browser;
 * - delivered: the relay echoed it back (mission.hitl.response with the same
 *   request and text), which it does only after passing it on to the run;
 * - refused: the relay answered delivery_failed for its request;
 * - failed: the socket was down, or no receipt came within
 *   ANSWER_RECEIPT_TIMEOUT_MS. A receipt that arrives later still turns it
 *   delivered.
 *
 * A failed or refused answer reopens its question, unless another answer
 * already closed it, and comes back as returnedAnswer for the composer to
 * restore.
 *
 * Every answer carries an answer_attempt_id (agion/Agion#2767): a fresh one
 * per send, the same one only when the user sends an unchanged answer again
 * after its delivery stayed uncertain (AnswerAttempts, @agion/mission-stream).
 * A host that admits answers replies with mission.question.answer.result:
 * accepted delivers the answer and shows the host's copy of it, refused
 * refuses it with the host's reason, and unknown fails it as not confirmed.
 * A refusal of any reason refuses only that attempt; mission.question.closed
 * keeps the question closed. An older host sends neither, and the echo and delivery_failed stay
 * the receipt and the refusal. Nothing is ever sent again on its own.
 *
 * Every callback is stable, because the chat builds its stream handlers once.
 */

import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import {
  ANSWER_RECEIPT_TIMEOUT_MS,
  AnswerAttempts,
  awaitsReceipt,
  isQuestionAnswerReason,
  nextDeliveryState,
  questionAnswerEvent,
  returnsToComposer,
  type AnswerIntent,
  type DeliveryEvent,
  type DeliveryState,
  type QuestionAnswerReason,
  type QuestionAnswerResult,
  type ReceiptReason,
  type UserMessageAttachment,
} from '@agion/mission-stream'
import type { QuestionReply } from '@agion/mission-stream'
import { getMissionStatusService } from '../services/streaming/mission-status'
import { useExecutionStore, type GlobalHITLRequest } from './useExecutionStore'
import type { ErrorMessage, HITLAttachment, HITLDecision } from '../types/streaming'
import type { ActiveHITLRequest, StreamingChatMessage } from './useStreamingMissionChat'

/**
 * Why a send failed: no connection, no receipt in time, or the run did not
 * start. For a message sent while the agent works (phase 6): the host did not
 * confirm it in time (it may be offline, or older and drop the frame), or the
 * run ended before the agent read it. For an answer (agion/Agion#2767): the
 * host answered that it cannot confirm it (unconfirmed).
 */
export type DeliveryFailure = 'not_connected' | 'no_receipt' | 'not_started' | 'not_accepted' | 'undelivered' | 'unconfirmed'

/** A sent message's delivery, kept on the message as data.delivery. */
export interface MessageDelivery {
  state: DeliveryState
  failure?: DeliveryFailure
  /** The reason the relay or the server gave, shown as it came. */
  detail?: string
  /** The typed reason of a receipt that refused it or did not deliver it (phase 6). */
  reason?: ReceiptReason
  /** The typed reason the host refused an answer with (agion/Agion#2767). */
  answerReason?: QuestionAnswerReason
}

/** Where an answer was given: the message box, or one of the question's choices. */
export type AnswerOrigin = 'composer' | 'choice'

/**
 * A failed or refused answer, handed back for the user to send again. A
 * message sent while the agent works comes back the same way, without a
 * question (phase 6).
 */
export interface ReturnedAnswer {
  /** Unique per return, so the composer restores each return once. */
  id: string
  /** The question the answer was for; none for a message sent while the agent works. */
  requestId?: string
  origin: AnswerOrigin
  text: string
  attachments: HITLAttachment[]
  delivery: MessageDelivery
  /**
   * The sends handed back before this one, oldest first, none with its own
   * `earlier` (phase 6). Several sends can come back in one tick, as when a
   * run end fails every message still waiting, and the composer takes every
   * one of them: returnsInOrder lists them all.
   */
  earlier?: ReturnedAnswer[]
}

/** Every send handed back so far, oldest first; `latest` is the channel's value. */
export function returnsInOrder(latest: ReturnedAnswer | null): ReturnedAnswer[] {
  if (!latest) return []
  const { earlier = [], ...last } = latest
  return [...earlier, last]
}

/** The channel's value for `returns`, oldest first: the last one, carrying the ones before it. */
function asReturned(returns: ReturnedAnswer[]): ReturnedAnswer | null {
  const last = returns[returns.length - 1]
  if (!last) return null
  const earlier = returns.slice(0, -1)
  return earlier.length > 0 ? { ...last, earlier } : last
}

interface TrackedAnswer {
  messageId: string
  question: ActiveHITLRequest
  /** The sidebar's copy of the question, put back when the answer returns. */
  sidebarEntry?: GlobalHITLRequest
  origin: AnswerOrigin
  text: string
  attachments: HITLAttachment[]
  delivery: MessageDelivery
  /** The answer_attempt_id it was sent with, and what it said. */
  attemptId: string
  intent: AnswerIntent
  timer?: ReturnType<typeof setTimeout>
}

/** The files as a chat row shows them. */
export function rowFiles(attachments: readonly UserMessageAttachment[] | undefined) {
  return attachments?.length
    ? attachments.map((a) => ({ filename: a.filename, contentType: a.content_type, size: a.size }))
    : undefined
}

interface UseAnswerDeliveryOptions {
  setMessages: Dispatch<SetStateAction<StreamingChatMessage[]>>
  /** Takes a question out of the open ones. Must be stable. */
  closeQuestion: (requestId: string) => void
  /** Puts a question back among the open ones and selects it. Must be stable. */
  reopenQuestion: (question: ActiveHITLRequest) => void
}

export interface AnswerDelivery {
  /** Sends an answer to an open question and tracks its delivery. */
  /**
   * decision: only for an approval card decided inline (agion/Agion#2618).
   * reply: only from a structured question's card; text is then what the row
   * shows, and the runner reads the reply (agion/Agion#3559).
   */
  sendAnswer: (missionId: string, question: ActiveHITLRequest, text: string, attachments: HITLAttachment[], origin: AnswerOrigin, decision?: HITLDecision, reply?: QuestionReply) => void
  /**
   * An echo of an answer; the id of this chat's message it is the receipt of,
   * or null. The echo's attempt id finds the message; an echo without one
   * matches by text.
   */
  receipt: (requestId: string, response: string, attemptId?: string) => string | null
  /** An error frame; true when it refused an answer this chat sent. */
  refusal: (error: ErrorMessage) => boolean
  /**
   * A host's result for an answer attempt (agion/Agion#2767); the id of this
   * chat's message it is about, or null.
   */
  questionResult: (result: QuestionAnswerResult) => string | null
  /** The host closed a question: it stays closed, and no answer reopens it. */
  questionClosed: (requestId: string) => void
  /** Forgets every tracked answer, for a new execution. */
  reset: () => void
  /** The latest send handed back, carrying the earlier ones (returnsInOrder). */
  returnedAnswer: ReturnedAnswer | null
  /**
   * Hands another send back to the composer (phase 6), after every send
   * already handed back: the one channel for answers and messages. Stable.
   */
  returnSend: (returned: ReturnedAnswer) => void
}

/**
 * The latest tracked answer to a request that `matches` and can still change
 * (sending or failed), preferring one still waiting for its receipt.
 */
function latestAnswer(answers: Map<string, TrackedAnswer>, requestId: string, matches: (a: TrackedAnswer) => boolean): TrackedAnswer | undefined {
  const latestFirst = [...answers.values()]
    .filter((a) => a.question.requestId === requestId && matches(a) && (awaitsReceipt(a.delivery.state) || a.delivery.state === 'failed'))
    .reverse()
  return latestFirst.find((a) => awaitsReceipt(a.delivery.state)) ?? latestFirst[0]
}

export function useAnswerDelivery({ setMessages, closeQuestion, reopenQuestion }: UseAnswerDeliveryOptions): AnswerDelivery {
  const answersRef = useRef(new Map<string, TrackedAnswer>())
  // Questions an echo, an accepted result or the host has closed: a returned
  // answer does not reopen them.
  const answeredRef = useRef(new Set<string>())
  const attemptsRef = useRef(new AnswerAttempts())
  const sequenceRef = useRef(0)
  const [returnedAnswer, setReturnedAnswer] = useState<ReturnedAnswer | null>(null)

  // Appends, so a send handed back in the same tick as another never
  // replaces it.
  const handBack = useCallback((returned: ReturnedAnswer) => {
    setReturnedAnswer((current) => asReturned([...returnsInOrder(current), returned]))
  }, [])

  const apply = useCallback((answer: TrackedAnswer, event: DeliveryEvent, why: Omit<MessageDelivery, 'state'> = {}) => {
    const before = answer.delivery.state
    const state = nextDeliveryState(before, event)
    if (state === before) return
    attemptsRef.current.record(answer.intent, answer.attemptId, event)
    const delivery: MessageDelivery = { state, ...why }
    answer.delivery = delivery
    if (!awaitsReceipt(state) && answer.timer !== undefined) {
      clearTimeout(answer.timer)
      answer.timer = undefined
    }
    setMessages((prev) => prev.map((m) => (m.id === answer.messageId ? { ...m, data: { ...m.data, delivery } } : m)))

    const { requestId } = answer.question
    if (returnsToComposer(state) && !returnsToComposer(before)) {
      if (!answeredRef.current.has(requestId)) {
        reopenQuestion(answer.question)
        const sidebar = useExecutionStore.getState()
        if (answer.sidebarEntry && !sidebar.hitlRequests.has(requestId)) sidebar.addHITLRequest(answer.sidebarEntry)
      }
      handBack({
        id: `${answer.messageId}-${state}`,
        requestId,
        origin: answer.origin,
        text: answer.text,
        attachments: answer.attachments,
        delivery,
      })
    }
    if (state === 'delivered') {
      closeQuestion(requestId)
      // A late receipt takes back this answer's own return only; another
      // send's text stays in the composer.
      const own = `${answer.messageId}-`
      setReturnedAnswer((current) => {
        const returns = returnsInOrder(current)
        const kept = returns.filter((r) => r.requestId !== requestId || !r.id.startsWith(own))
        return kept.length === returns.length ? current : asReturned(kept)
      })
    }
  }, [setMessages, closeQuestion, reopenQuestion, handBack])

  const sendAnswer = useCallback<AnswerDelivery['sendAnswer']>((missionId, question, text, attachments, origin, decision, reply) => {
    sequenceRef.current += 1
    const intent: AnswerIntent = {
      requestId: question.requestId,
      text,
      fileIds: attachments.map((a) => a.host_file_id),
      ...(decision ? { decision } : {}),
      ...(reply?.selected_option_ids ? { selectedOptionIds: reply.selected_option_ids } : {}),
      ...(reply?.other_text ? { otherText: reply.other_text } : {}),
    }
    const answer: TrackedAnswer = {
      messageId: `user-msg-${question.requestId}-${sequenceRef.current}`,
      question,
      sidebarEntry: useExecutionStore.getState().hitlRequests.get(question.requestId),
      origin,
      text,
      attachments,
      delivery: { state: 'waiting' },
      attemptId: attemptsRef.current.attemptFor(intent),
      intent,
    }
    answersRef.current.set(answer.messageId, answer)
    setMessages((prev) => [...prev, {
      id: answer.messageId,
      type: 'hitl_response',
      timestamp: Date.now(),
      missionId,
      data: {
        requestId: question.requestId,
        response: text,
        attachments: rowFiles(attachments),
        deliveryOrigin: origin,
        delivery: answer.delivery,
        // The member's own answer until the relay's echo names its author (agion/Agion#2039).
        sentByThisChat: true,
      },
    }])
    closeQuestion(question.requestId)
    useExecutionStore.getState().removeHITLRequest(question.requestId)

    const sent = getMissionStatusService().sendHITLResponse({
      missionId,
      executionId: question.executionId,
      nodeId: question.nodeId,
      requestId: question.requestId,
      response: text,
      responseTimeMs: Date.now() - question.receivedAt,
      attachments: attachments.length ? attachments : undefined,
      ...(decision ? { decision } : {}),
      answerAttemptId: answer.attemptId,
      ...(reply ? { answer: reply } : {}),
    })
    // Only an explicit false means nothing left the browser.
    if (sent === false) {
      apply(answer, 'not_sent', { failure: 'not_connected' })
      return
    }
    apply(answer, 'sent')
    answer.timer = setTimeout(() => apply(answer, 'timeout', { failure: 'no_receipt' }), ANSWER_RECEIPT_TIMEOUT_MS)
  }, [setMessages, closeQuestion, apply])

  /** The tracked answer sent with this attempt: the latest that can still change, else the latest. */
  const byAttempt = useCallback((requestId: string, attemptId: string): TrackedAnswer | undefined => {
    const sentWith = (a: TrackedAnswer) => a.attemptId === attemptId
    return latestAnswer(answersRef.current, requestId, sentWith)
      ?? [...answersRef.current.values()].reverse().find((a) => a.question.requestId === requestId && sentWith(a))
  }, [])

  /** The question is over: no returned answer reopens it, and its card and sidebar entry close. */
  const closeForGood = useCallback((requestId: string) => {
    answeredRef.current.add(requestId)
    attemptsRef.current.forget(requestId)
    closeQuestion(requestId)
    useExecutionStore.getState().removeHITLRequest(requestId)
  }, [closeQuestion])

  const receipt = useCallback((requestId: string, response: string, attemptId?: string): string | null => {
    answeredRef.current.add(requestId)
    const answer = attemptId
      ? byAttempt(requestId, attemptId)
      : latestAnswer(answersRef.current, requestId, (a) => a.text === response)
    if (!answer) return null
    apply(answer, 'receipt')
    return answer.messageId
  }, [apply, byAttempt])

  const refusal = useCallback((error: ErrorMessage): boolean => {
    if (error.code !== 'delivery_failed' || !error.request_id) return false
    const answer = error.answer_attempt_id
      ? byAttempt(error.request_id, error.answer_attempt_id)
      : latestAnswer(answersRef.current, error.request_id, () => true)
    if (!answer) return false
    apply(answer, 'refusal', { detail: error.message })
    return true
  }, [apply, byAttempt])

  const questionResult = useCallback((result: QuestionAnswerResult): string | null => {
    const { answer: sent, outcome, reason } = result
    const known = isQuestionAnswerReason(reason) ? reason : undefined
    const event = questionAnswerEvent(outcome)
    const answer = sent.answer_attempt_id ? byAttempt(sent.request_id, sent.answer_attempt_id) : undefined
    if (!answer || !event) return null
    switch (event) {
      case 'receipt': {
        answeredRef.current.add(sent.request_id)
        apply(answer, event)
        // The answer as the host took it: its files carry the host's names and types.
        const accepted = { response: sent.response, attachments: rowFiles(sent.attachments) }
        setMessages((prev) => prev.map((m) => (m.id === answer.messageId ? { ...m, data: { ...m.data, ...accepted } } : m)))
        break
      }
      case 'refusal':
        apply(answer, event, known ? { answerReason: known } : {})
        break
      default:
        apply(answer, event, { failure: 'unconfirmed' })
    }
    return answer.messageId
  }, [apply, byAttempt, setMessages])

  const questionClosed = useCallback((requestId: string) => {
    closeForGood(requestId)
  }, [closeForGood])

  const reset = useCallback(() => {
    for (const answer of answersRef.current.values()) {
      if (answer.timer !== undefined) clearTimeout(answer.timer)
    }
    answersRef.current = new Map()
    answeredRef.current = new Set()
    attemptsRef.current.clear()
    setReturnedAnswer(null)
  }, [])

  useEffect(() => () => {
    for (const answer of answersRef.current.values()) {
      if (answer.timer !== undefined) clearTimeout(answer.timer)
    }
  }, [])

  return { sendAnswer, receipt, refusal, questionResult, questionClosed, reset, returnedAnswer, returnSend: handBack }
}
